import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { API, api } from "../lib/api";
import { Volume2, X, Pause } from "lucide-react";

const SEEN_KEY = "raq_tips_seen";
const OFF_KEY = "raq_tips_off";
const seen = () => { try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"); } catch { return []; } };

export default function RaqTips() {
  const { pathname } = useLocation();
  const [tips, setTips] = useState(null);
  const [tip, setTip] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("raqtips") === "off") localStorage.setItem(OFF_KEY, "1");
    api.get("/learn/tips").then((r) => setTips(r.data.tips || [])).catch(() => setTips([]));
  }, []);

  useEffect(() => {
    if (!tips || localStorage.getItem(OFF_KEY) === "1") return;
    const t = tips.find((x) => new RegExp(x.match).test(pathname));
    if (!t || seen().includes(t.key)) { setTip(null); return; }
    const es = localStorage.getItem("raq_lang") === "es" && t.text_es;
    setTip({ ...t, title: es ? t.title_es : t.title, text: es ? t.text_es : t.text, es: !!es });
    const a = new Audio(`${API}/learn/media/${es ? t.audio_es : t.audio}`); audioRef.current = a;
    a.onended = () => setPlaying(false);
    a.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    return () => { a.pause(); audioRef.current = null; setPlaying(false); };
  }, [tips, pathname]);

  if (!tip) return null;
  const dismiss = () => { localStorage.setItem(SEEN_KEY, JSON.stringify([...new Set([...seen(), tip.key])])); audioRef.current?.pause(); setTip(null); };
  const toggle = () => { const a = audioRef.current; if (!a) return; if (a.paused) a.play().then(() => setPlaying(true)).catch(() => {}); else { a.pause(); setPlaying(false); } };
  const off = () => { localStorage.setItem(OFF_KEY, "1"); dismiss(); };

  return (
    <div className="fixed bottom-5 left-4 sm:left-6 z-40 w-[calc(100vw-2rem)] sm:w-[360px] rounded-2xl border border-cyan-500/30 bg-[#0B0D12]/95 backdrop-blur-xl shadow-[0_20px_50px_-15px_rgba(34,211,238,.35)] p-4 rise" data-testid="raq-tip" role="status">
      <div className="flex gap-3 items-start">
        <img src="/raq-avatar.png" alt="Raq" className={`w-12 h-12 rounded-full shrink-0 ${playing ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-[#0B0D12]" : ""}`} />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-[10px] tracking-widest uppercase text-cyan-400">Raq · {tip.title}</p>
          <p className="text-sm text-slate-200 mt-1 leading-relaxed" data-testid="raq-tip-text">{tip.text}</p>
          <div className="flex items-center gap-2 mt-3">
            <button onClick={toggle} data-testid="raq-tip-play" className="h-8 px-3 rounded-full bg-cyan-400 text-slate-950 text-xs font-semibold flex items-center gap-1.5 hover:bg-cyan-300">{playing ? <Pause className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}{playing ? (tip.es ? "Pausar" : "Pause") : (tip.es ? "Escuchar a Raq" : "Hear Raq")}</button>
            <button onClick={dismiss} data-testid="raq-tip-dismiss" className="h-8 px-3 rounded-full border border-slate-700 text-slate-300 text-xs hover:border-slate-500">{tip.es ? "Entendido" : "Got it"}</button>
            <button onClick={off} data-testid="raq-tip-off" className="ml-auto text-[11px] text-slate-500 hover:text-slate-300">{tip.es ? "Desactivar consejos" : "Turn off tips"}</button>
          </div>
        </div>
        <button onClick={dismiss} aria-label="Close tip" data-testid="raq-tip-close" className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
